import { StatusBadgeVariant } from "../../../../common/components/xepp/status-badge/StatusBadge";
import { RefundAttempt, RefundCaseStatus } from "../../../../model/refunds/refund";

// Translation-key fragments under utilityAdmin:refunds.*; the pages assemble the full key so
// the typed t() can check it.

export type RefundStatusKey = "inProgress" | "refunded" | "failed";

export const refundStatusKey = (status: RefundCaseStatus | string): RefundStatusKey => {
	if (status === "Refunded") return "refunded";
	if (status === "InProgress") return "inProgress";
	return "failed";
};

const STATUS_VARIANTS: Record<RefundStatusKey, StatusBadgeVariant> = {
	refunded: "refunded",
	inProgress: "atRisk",
	failed: "breached",
};

export const refundStatusVariant = (status: RefundCaseStatus | string): StatusBadgeVariant => STATUS_VARIANTS[refundStatusKey(status)];

export type AttemptStatusKey = "inProgress" | "refunded" | "failed" | "queuedForManualReview" | "unknown";

export const attemptStatusKey = (status: string): AttemptStatusKey => {
	switch (status) {
		case "InProgress":
			return "inProgress";
		case "Refunded":
			return "refunded";
		case "Failed":
			return "failed";
		case "QueuedForManualReview":
			return "queuedForManualReview";
		default:
			return "unknown";
	}
};

const ATTEMPT_VARIANTS: Record<AttemptStatusKey, StatusBadgeVariant> = {
	inProgress: "atRisk",
	refunded: "refunded",
	failed: "breached",
	queuedForManualReview: "breached",
	unknown: "unknown",
};

export const attemptStatusVariant = (status: string): StatusBadgeVariant => ATTEMPT_VARIANTS[attemptStatusKey(status)];

export type ChannelKey = "card" | "cash" | "transfer" | "wallet" | "oneTimeWallet" | "friendRequest" | "unknown";

const CHANNEL_KEYS: Record<string, ChannelKey> = {
	Card: "card",
	Cash: "cash",
	Transfer: "transfer",
	Wallet: "wallet",
	OneTimeWallet: "oneTimeWallet",
	FriendRequest: "friendRequest",
};

export const channelKey = (paymentChannel: string): ChannelKey => CHANNEL_KEYS[paymentChannel] ?? "unknown";

export type DestinationKey = "wallet" | "card" | "unknown";

export const destinationKey = (refundDestination: string): DestinationKey => {
	if (refundDestination === "Wallet") return "wallet";
	if (refundDestination === "Card") return "card";
	return "unknown";
};

/** Who an attempt is shown as: a named admin, or a translated placeholder for the rest. */
export type ActorLabel = { name: string } | { key: "system" | "admin" };

export const actorLabel = (attempt: Pick<RefundAttempt, "requestSource" | "actorName">): ActorLabel => {
	if (attempt.requestSource !== "UtilityAdmin") return { key: "system" };
	return attempt.actorName ? { name: attempt.actorName } : { key: "admin" };
};

export const attemptsLabel = (attemptsUsed: number, maxAttempts: number): string => `${attemptsUsed}/${maxAttempts}`;

export type RelativeTimeUnit = "justNow" | "minute" | "minutes" | "hour" | "hours" | "day" | "days";

const MAX_RELATIVE_DAYS = 30;

/**
 * "2mins ago"-style parts for the Updated column; null when the date is unusable or older than
 * a month, in which case the caller shows the absolute date instead.
 */
export const relativeTimeParts = (iso: string, now: number = Date.now()): { unit: RelativeTimeUnit; count: number } | null => {
	const time = new Date(iso).getTime();
	if (Number.isNaN(time)) return null;

	const minutes = Math.floor(Math.max(0, now - time) / 60_000);
	if (minutes < 1) return { unit: "justNow", count: 0 };
	if (minutes < 60) return { unit: minutes === 1 ? "minute" : "minutes", count: minutes };

	const hours = Math.floor(minutes / 60);
	if (hours < 24) return { unit: hours === 1 ? "hour" : "hours", count: hours };

	const days = Math.floor(hours / 24);
	if (days > MAX_RELATIVE_DAYS) return null;
	return { unit: days === 1 ? "day" : "days", count: days };
};

export const refundDetailsPath = (basePath: string, transactionId: string): string => `${basePath}/${encodeURIComponent(transactionId)}`;
